import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import {
  IAppState,
  IFilterFloor,
  Wings,
} from 'src/app/shared/state-management/state-interfaces';
import { NgRedux } from '@angular-redux/store';
import { IFloor } from '../floor-management-interface';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-filter-floors',
  templateUrl: './filter-floors.component.html',
  styleUrls: ['./filter-floors.component.scss'],
})
export class FilterFloorsComponent {
  stepper: number = 1;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  floors: IFloor[] | undefined;
  filter!: IFilterFloor;
  readonly notSpecified = this.translate.instant('NOT_SPECIFIED');

  constructor(
    public dialogRef: MatDialogRef<FilterFloorsComponent>,
    private helperService: HelperService,
    @Inject(MAT_DIALOG_DATA) objectFloor: IFilterFloor,
    private translate: TranslateService
  ) {
    this.filter = {
      wings: [...objectFloor.wings!],
      wingsSelected: [...objectFloor.wingsSelected!],
      floors: [...objectFloor.floors!],
      floorsSelected: [...objectFloor.floorsSelected!],
      filterType: objectFloor.filterType,
    };
  }

  /**
   * @description: closeDialog function close the dialogRef.
   * @input none
   * @output { void }
   */

  closeDialog() {
    if (
      this.filter.floorsSelected.length === 0 &&
      this.filter.wingsSelected.length === 0
    ) {
      this.dialogRef?.close();
      return;
    }
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.dialogRef?.close();
      }
    });
  }

  /**
   * @description: navigateBack function set the step to 1.
   * @input none
   * @output { void }
   */
  navigateBack() {
    this.stepper = 1;
  }

  /**
   * @description: #changeStep function set the step to 2 for Wings
   *               and 3 for Floors.
   * @param { string } step
   * @output { void }
   */
  changeStep(step: string) {
    if (step === CONSTANTS.WING) this.stepper = 2;
    else if (step === CONSTANTS.FLOORS) this.stepper = 3;
  }

  /**
   * @description: #selectFloor function accepts selected floor from list and emit selectedFloor to parent component.
   * @input { string }
   * @param { floorId , floorName }
   * @output { void }
   */

  selectFloor(floorId: string, floorName: string) {
    let updatedFloor = this.filter?.floors?.map((floor: IFloor) => {
      if (
        floor?.floorId === floorId &&
        floor?.floorState === CONSTANTS.NOT_SELECTED
      ) {
        this.updateFloorDescription(CONSTANTS.ADD, floorName);
        return { ...floor, floorState: CONSTANTS.SELECTED };
      }
      if (
        floor?.floorId === floorId &&
        floor?.floorState === CONSTANTS.SELECTED
      ) {
        this.updateFloorDescription(CONSTANTS.REMOVE, floorName);
        return { ...floor, floorState: CONSTANTS.NOT_SELECTED };
      }
      return floor;
    });
    this.filter.floors = updatedFloor;
  }

  /**
   * @description: #updateFloorDescription function updates floors selected in filter.
   * @param {string } action : remove | add | resetAll
   * @param {string } floorName
   * @output { void }
   */

  updateFloorDescription(action: string, floorName: string): void {
    if (action === CONSTANTS.REMOVE) {
      this.filter.floorsSelected = this.filter.floorsSelected.filter(
        (name) => name !== floorName
      );
    } else if (action === CONSTANTS.ADD) {
      this.filter.floorsSelected.push(floorName);
      this.filter.floorsSelected.sort();
    } else if (action === CONSTANTS.RESET_ALL) {
      this.filter.floorsSelected = []; // Reset All Floors case
    }
  }

  /**
   * @description: #selectWing function accepts selected floor from list and emit selectedFloor to parent component.
   * @input { string }
   * @param { wingName }
   * @output { void }
   */

  selectWing(wingName: string) {
    let updatedFloors = this.filter.floors?.map((floor: IFloor) => {
      if (
        floor?.wingName === wingName &&
        floor?.wingState === CONSTANTS.NOT_SELECTED
      ) {
        return { ...floor, wingState: CONSTANTS.SELECTED };
      } else if (
        floor?.wingName === wingName &&
        floor?.wingState === CONSTANTS.SELECTED
      ) {
        return { ...floor, wingState: CONSTANTS.NOT_SELECTED };
      }
      return floor;
    });
    this.filter.floors = updatedFloors;

    let updatedWings = this.filter?.wings.map((wing: any) => {
      if (
        wing.wingName === wingName &&
        wing.wingState === CONSTANTS.NOT_SELECTED
      ) {
        this.updateWingDescription(CONSTANTS.ADD, wingName);
        return { ...wing, wingState: CONSTANTS.SELECTED };
      } else if (
        wing.wingName === wingName &&
        wing.wingState === CONSTANTS.SELECTED
      ) {
        this.updateWingDescription(CONSTANTS.REMOVE, wingName);
        return { ...wing, wingState: CONSTANTS.NOT_SELECTED };
      }
      return wing;
    });
    this.filter.wings = updatedWings;
  }

  /**
   * @description: #updateWingDescription function updates wings selected in filter.
   * @param {string } action : remove | add
   * @output { void }
   */

  updateWingDescription(action: string, wingName: string): void {
    if (action === CONSTANTS.REMOVE) {
      this.filter.wingsSelected = this.filter.wingsSelected.filter(
        (name) => name !== wingName
      );
    } else if (action === CONSTANTS.ADD) {
      this.filter.wingsSelected.push(wingName);
      this.filter.wingsSelected.sort();
    } else if (action === CONSTANTS.RESET_ALL) {
      this.filter.wingsSelected = []; // Reset All Wings case
    }
  }

  /**
   * @description: resetFilter Function acepts type as parameter and decide which filter to be removed.
   * @input { string }
   * @param { type }
   * @output { void }
   */

  resetFilter(type: string) {
    if (type === CONSTANTS.FLOORS) {
      this.resetFloors();
    }

    if (type === CONSTANTS.WINGS) {
      this.resetWings();
    }
    if (type === CONSTANTS.RESET_ALL) {
      this.resetFloors();
      this.resetWings();
    }
  }

  /**
   * @description: resetFloors function reset all the floors selected from floorList component.
   * @input { none }
   * @output { void }
   */

  resetFloors() {
    let updatedFloors = this.filter.floors?.map((floor: IFloor) => {
      return { ...floor, floorState: CONSTANTS.NOT_SELECTED };
    });
    this.filter.floors = updatedFloors;
    this.updateFloorDescription(CONSTANTS.RESET_ALL, CONSTANTS.REMOVE);
  }

  /**
   * @description: resetWings function reset all the wings selected from wingList component.
   * @input { none }
   * @output { void }
   */

  resetWings() {
    let updatedWings = this.filter.wings?.map((wing: any) => {
      return { ...wing, wingState: CONSTANTS.NOT_SELECTED };
    });
    let updatedFloors = this.filter.floors?.map((floor: IFloor) => {
      return { ...floor, wingState: CONSTANTS.NOT_SELECTED };
    });
    this.filter.floors = updatedFloors;
    this.filter.wings = updatedWings;
    this.updateWingDescription(CONSTANTS.RESET_ALL, CONSTANTS.REMOVE);
  }

  /**
   * @description: #applyFilter function will apply the final changes
   *                to the filterFloor aftreClosed subscriber.
   * @output { void }
   */
  applyFilter(): void {
    this.dialogRef?.close(this.filter);
  }
}
